/**
 * Copied from https://github.com/mantinedev/mantine/blob/master/src/mantine-hooks/src/use-focus-trap/use-focus-trap.ts#L6
 * to replace the `scopeTab()` implementation with one that properly handles shift + tab back to RadioGroups.
 * See: https://github.com/mantinedev/mantine/issues/4678
 */

import { useCallback, useEffect, useRef } from "react"
import { FOCUS_SELECTOR, focusable, tabbable } from "./tabbable"
import { scopeTab } from "./scope-tab"
import { createAriaHider } from "./create-aria-hider"

export function useFocusTrap(active = true): (instance: HTMLElement | null) => void {
    const ref = useRef<HTMLElement | null>()
    // eslint-disable-next-line @typescript-eslint/ban-types
    const restoreAria = useRef<Function | null>(null)

    const focusNode = (node: HTMLElement) => {
        let focusElement: HTMLElement = node.querySelector("[data-autofocus]")

        if (!focusElement) {
            const children = Array.from<HTMLElement>(node.querySelectorAll(FOCUS_SELECTOR))
            focusElement = children.find(tabbable) || children.find(focusable) || null
            if (!focusElement && focusable(node)) focusElement = node
        }

        if (focusElement) {
            focusElement.focus({ preventScroll: true })
        } else if (process.env.NODE_ENV === "development") {
            // eslint-disable-next-line no-console
            console.warn("[@mantine/hooks/use-focus-trap] Failed to find focusable element within provided node", node)
        }
    }

    const setRef = useCallback(
        (node: HTMLElement | null) => {
            if (!active) {
                return
            }

            if (node === null) {
                if (restoreAria.current) {
                    restoreAria.current()
                    restoreAria.current = null
                }
                return
            }

            restoreAria.current = createAriaHider(node)
            if (ref.current === node) {
                return
            }

            if (node) {
                // Delay processing the HTML node by a frame. This ensures focus is assigned correctly.
                setTimeout(() => {
                    if (node.getRootNode()) {
                        focusNode(node)
                    } else if (process.env.NODE_ENV === "development") {
                        // eslint-disable-next-line no-console
                        console.warn("[@mantine/hooks/use-focus-trap] Ref node is not part of the dom", node)
                    }
                })

                ref.current = node
            } else {
                ref.current = null
            }
        },
        [active]
    )

    useEffect(() => {
        if (!active) {
            return undefined
        }

        ref.current && setTimeout(() => focusNode(ref.current))

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Tab" && ref.current) {
                scopeTab(ref.current, event)
            }
        }

        document.addEventListener("keydown", handleKeyDown)
        return () => {
            document.removeEventListener("keydown", handleKeyDown)

            if (restoreAria.current) {
                restoreAria.current()
            }
        }
    }, [active])

    return setRef
}
